import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import classNames from 'clsx';
import { AppQueryParams, AppSections } from '../../constants';
import { useApplicationParams, useLanguage, useUserProfile } from '../../context';
import { useEventTracker, useProductDetails, useWindowWidth } from '../hooks';
import BackHeader from '../layout/BackHeader';
import ProductImage from '../shop/ProductImage';
import Carousel from '../shop/Carousel';
import CustomModal from '../shop/Modal';
import ProductDetailsAction from '../shop/ProductDetailsAction';
import HeaderWeb from '../layout/HeaderWeb';
import CustomerSupportButton from '../shop/CustomerSupportButton';
import Icons from '../../constants/Icons';

const addLeadingZeros = (value) => {
  value = String(value);
  while (value.length < 2) {
    value = '0' + value;
  }
  return value;
};

const ProductDetails = () => {
  const { t } = useTranslation();

  const trackEvent = useEventTracker();

  const { isWeb, userToken, appSection } = useApplicationParams();
  const { language } = useLanguage();

  const userProfile = useUserProfile();

  const {
    productDetails: productDetailsData,
    isLoading: isProductDetailsLoading,
    productId,
  } = useProductDetails();

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const windowWidth = useWindowWidth();

  const isMobile = windowWidth <= 414;
  const operationResult = searchParams.get('status') || '';
  const isLiveRoomsSection = appSection === AppSections.LiveRoom;
  const isUserVip = userProfile?.isPaid ?? false;
  const isProductAvailable =
    isProductDetailsLoading || (productDetailsData && productDetailsData.length > 0);

  const [selectedProductIndex, setSelectedProductIndex] = useState(null);
  const [expiryCountdown, setExpiryCountdown] = useState('');
  const [errorModal, setErrorModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);

  const productDetails = productDetailsData?.[selectedProductIndex] || {};

  const productExpirationDate = productDetails.expirationDate;

  const productStock = productDetails?.stock ?? 0;

  const productImages =
    productDetails.images?.length > 0
      ? [
          ...new Set(
            productDetailsData
              ?.map((product) => [
                product.backgroundImageURL,
                ...product.images.map((i) => i.imageURL),
              ])
              ?.flat(),
          ),
        ]
      : [productDetails.backgroundImageURL];

  const availableSizes =
    productDetailsData?.length && productDetailsData.length > 1 && productDetails.attributes?.size
      ? productDetailsData.map((p) => ({
          id: p.id,
          size: p.attributes.size,
          stock: p.stock,
          billingOptions: p.billingOptions,
        }))
      : [];

  useEffect(() => {
    if (productDetailsData) {
      const foundProductIndex = productDetailsData.findIndex(
        (el) => el.billingOptions.length && el.stock > 0,
      );

      setSelectedProductIndex(foundProductIndex !== -1 ? foundProductIndex : 0);
    }
  }, [productDetailsData]);

  useEffect(() => {
    if (productExpirationDate) {
      setInterval(() => {
        const then = moment(productExpirationDate, 'YYYY-MM-DD hh:mm:ss');
        const now = moment();
        const countdown = then.diff(now);
        const tempTime = moment.duration(countdown);
        const days = tempTime.days();
        const hours = addLeadingZeros(tempTime.hours());
        const minutes = addLeadingZeros(tempTime.minutes());
        const seconds = addLeadingZeros(tempTime.seconds());

        setExpiryCountdown(
          days && days > 0
            ? `${days} ${t(days > 1 ? 'days_left' : 'day_left')}`
            : `${hours}:${minutes}:${seconds}`,
        );
      }, 1000);
    }
  }, [productExpirationDate]);

  const handleRedeem = () => {
    const finalProductId = productDetails.id || productId;

    trackEvent('redeem_product_clicked', {
      source: 'shop',
      product_id: finalProductId,
      is_guest: !userToken,
    });

    trackEvent('redeem_points_screen', {
      product_id: finalProductId,
      product_name: productDetails.name,
    });
  };

  const handleBuy = () => {
    const finalProductId = productDetails.id || productId;

    trackEvent('buy_product_clicked', {
      source: 'shop',
      product_id: finalProductId,
      is_guest: !userToken,
    });
  };

  useEffect(() => {
    if (!productDetails.id || !operationResult) {
      return;
    }

    if (operationResult === 'success') {
      setSuccessModal(true);

      trackEvent('buy_product_result', {
        product_id: productDetails.id,
        product_name: productDetails.name,
      });
    }

    if (operationResult === 'error') {
      setErrorModal(true);

      trackEvent('product_purchase_failed', {
        product_id: productDetails.id,
        product_name: productDetails.name,
      });
    }
  }, [operationResult, productDetails]);

  const storedSection = sessionStorage.getItem(`${AppSections.LiveRoom}-section`);

  const productDetailsSectionClassNames = classNames({
    'handle-fixed-nav': !isWeb && !isLiveRoomsSection,
  });

  return (
    <>
      {isWeb && !isMobile && (
        <>
          <HeaderWeb />
          <CustomerSupportButton />
        </>
      )}
      {isProductAvailable && (
        <div className="section-wrapper">
          {/* Should be discussed logic for back button */}
          {(location.state?.isBackAllowed || storedSection || !isLiveRoomsSection) && (
            <BackHeader
              title={t('product_details')}
              url={
                (location.state?.isBackAllowed || storedSection) && isLiveRoomsSection
                  ? storedSection
                    ? `/shop/sections/${storedSection}?${AppQueryParams.AppSection}=${AppSections.LiveRoom}`
                    : null
                  : !isWeb
                  ? '/shop?userToken=' + userToken
                  : '/shop'
              }
            />
          )}
          <section className={productDetailsSectionClassNames}>
            {!isWeb && !isLiveRoomsSection && <div className="product-details-section-separator" />}
            {isProductDetailsLoading ? (
              <div className="loader mt-5" />
            ) : isWeb ? (
              <Row className="product-details-web">
                <Col
                  xs={isMobile ? 12 : 6}
                  className="product-details-img-div product-details-img-div-lg"
                >
                  {productImages && productImages.length > 0 && (
                    <Carousel
                      ItemComponent={ProductImage}
                      data={productImages}
                      itemsToShow={1}
                      showDots
                      showArrows={productImages.length > 1}
                    />
                  )}
                  {isMobile && (
                    <div className="amount-left-div amount-center-div">
                      <img src={Icons.fire} className="fire-img mr-1" alt="" />
                      {productStock} {t('left')}
                    </div>
                  )}
                </Col>
                <Col xs={isMobile ? 12 : 6} className="product-details-div product-details-div-lg">
                  {expiryCountdown && (
                    <span className="product-expiration details">{expiryCountdown}</span>
                  )}
                  <div className="product-info-div product-info-div-lg">
                    {!isMobile && (
                      <div className="amount-left-div amount-center-div">
                        <img src={Icons.fire} className="fire-img mr-1" alt="" />
                        {productStock} {t('left')}
                      </div>
                    )}
                    <p className="product-details-name">{productDetails.name}</p>
                    <div>
                      {productDetails.price && productDetails.price > 0 && !isWeb && (
                        <span>
                          {language === 'ar' && (
                            <span className="diamond-text">{productDetails.price}</span>
                          )}
                          <img src={Icons.diamond} className="diamond-img2" alt="" />
                          {language === 'en' && (
                            <span className="diamond-text">{productDetails.price}</span>
                          )}
                        </span>
                      )}
                      {productDetails.priceMoney && productDetails.priceMoney > 0 && (
                        <span>
                          <span className="dollar-text">${productDetails.priceMoney}</span>
                        </span>
                      )}
                    </div>
                    <div className="product-details-sections">
                      {availableSizes && availableSizes.length > 0 && (
                        <p className="product-details-section-title">{t('size')}</p>
                      )}
                      <div className="product-details-sizes-div">
                        {availableSizes?.map((sizeItem, index) => {
                          const sizeButtonClassName = classNames('size-btn', {
                            selected: productDetails.attributes?.size === sizeItem.size,
                            disabled: sizeItem.stock <= 0 || !sizeItem.billingOptions.length,
                          });

                          return (
                            <button
                              key={sizeItem.id}
                              onClick={() => setSelectedProductIndex(index)}
                              className={sizeButtonClassName}
                              disabled={sizeItem.stock <= 0 || !sizeItem.billingOptions.length}
                            >
                              {sizeItem.size}
                            </button>
                          );
                        })}
                      </div>
                      {productDetails.description && (
                        <div className="mt-2">
                          <p className="product-details-section-title">{t('description')}</p>
                          <p>{productDetails.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <ProductDetailsAction
                    productDetails={productDetails}
                    isUserVip={isUserVip}
                    productId={productDetails.id || productId}
                    handleRedeem={handleRedeem}
                    handleBuy={handleBuy}
                    userToken={userToken}
                    isMobile={isMobile}
                    isWeb
                    isLiveRoomsSection={isLiveRoomsSection}
                  />
                </Col>
              </Row>
            ) : (
              <>
                <div className="product-details-div">
                  {/* <img src={vipBadge} className='vip-badge-img-lg' /> */}
                  {expiryCountdown && (
                    <span className="product-expiration details">{expiryCountdown}</span>
                  )}
                  <div className="product-details-img-div">
                    {/* {productImages && productImages.length === 1 && <ProductImage data={productImages[0]} />} */}
                    {productImages && productImages.length > 0 && (
                      <Carousel
                        ItemComponent={ProductImage}
                        data={productImages}
                        itemsToShow={1}
                        showDots
                      />
                    )}
                    <div className="amount-left-div">
                      <img src={Icons.fire} className="fire-img mr-1" alt="" />
                      {productStock} {t('left')}
                    </div>
                  </div>
                  <div className={`product-info-div ${!isWeb ? 'mobile' : ''}`}>
                    <p className="product-details-name">{productDetails.name}</p>
                    <div>
                      {productDetails.price && productDetails.price > 0 && (
                        <span>
                          {language === 'ar' && (
                            <span className="diamond-text">{productDetails.price}</span>
                          )}
                          <img src={Icons.diamond} className="diamond-img2" alt="" />
                          {/* <span className='diamond-text'>{productDetails.price}</span> */}
                          {language === 'en' && (
                            <span className="diamond-text">{productDetails.price}</span>
                          )}
                        </span>
                      )}

                      {productDetails.priceMoney && productDetails.priceMoney > 0 && (
                        <span>
                          <span className="dollar-text">${productDetails.priceMoney}</span>
                        </span>
                      )}
                    </div>

                    <div className="product-details-sections">
                      {availableSizes && availableSizes.length > 0 && (
                        <p className="product-details-section-title">{t('size')}</p>
                      )}
                      <div className="product-details-sizes-div">
                        {availableSizes?.map((sizeItem, index) => {
                          const sizeButtonClassName = classNames('size-btn', {
                            selected: productDetails.attributes?.size === sizeItem.size,
                            disabled: sizeItem.stock <= 0 || !sizeItem.billingOptions.length,
                          });

                          return (
                            <button
                              key={sizeItem.id}
                              onClick={() => setSelectedProductIndex(index)}
                              className={sizeButtonClassName}
                              disabled={sizeItem.stock <= 0 || !sizeItem.billingOptions.length}
                            >
                              {sizeItem.size}
                            </button>
                          );
                        })}
                      </div>
                      {productDetails.description && (
                        <div className="mt-2">
                          <p className="product-details-section-title">{t('description')}</p>
                          {/* <p className='product-details-description'>{productDetails.description}</p> */}
                          <p
                            dangerouslySetInnerHTML={{
                              __html: productDetails.description,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <ProductDetailsAction
                  productDetails={productDetails}
                  isUserVip={isUserVip}
                  handleBuy={handleBuy}
                  handleRedeem={handleRedeem}
                  productId={productDetails.id || productId}
                  userToken={userToken}
                  isMobile={isMobile}
                  isWeb={isWeb}
                  isLiveRoomsSection={isLiveRoomsSection}
                />
              </>
            )}
          </section>
        </div>
      )}
      <CustomModal
        icon={<img alt="" src={Icons.errorIcon} width={140} />}
        description={<h5>{t('smth_went_wrong')}</h5>}
        text={t('ok')}
        show={errorModal}
        onHide={() => setErrorModal(false)}
        isLiveRoomsSection={isLiveRoomsSection}
      />
      <CustomModal
        className="success"
        icon={<img alt="" src={Icons.success} width={140} />}
        description={
          <>
            <h5>{t('order_placed')}</h5>
            <p>{t('order_placed_successfully')}</p>
          </>
        }
        text={t(isLiveRoomsSection ? 'go_back_to_live' : 'continue_shopping')}
        show={successModal}
        action
        onHide={() => setSuccessModal(false)}
        isLiveRoomsSection={isLiveRoomsSection}
      />
      <CustomModal
        action
        icon={<img alt="" src={Icons.errorIcon} width={140} />}
        description={
          <>
            <h5>{t('product_unavailable')}</h5>
            <p>{t('product_unavailable_description')}</p>
          </>
        }
        text={t(isLiveRoomsSection ? 'go_back_to_live' : 'continue_shopping')}
        show={!isProductAvailable}
        isLiveRoomsSection={isLiveRoomsSection}
      />
    </>
  );
};

export default ProductDetails;
